import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Calendar,
  Clock,
  User,
  Scissors,
  Ban,
  Check,
  AlertTriangle,
  UserX,
  Timer,
  Phone,
  History
} from 'lucide-react';
import { format, parseISO, differenceInMinutes } from 'date-fns';
import { he } from 'date-fns/locale';
import { supabase } from '../../../../lib/supabase';
import { useAuth } from '../../../../contexts/auth/hooks';
import { useAppointmentStatus } from '../../../../hooks/useAppointmentStatus';
import toast from 'react-hot-toast';
import { normalizeForStorage, normalizeDate, localToUtc } from '../../../../utils/date';
import { isValidPhoneNumber } from '../../../../utils/validation'; // Assume you have or add this util
import ChargeCustomerFlow from '../../../ChargeCustomerFlow'; // ודא שיש לך את הקומפוננטה הזו

interface StaffMember {
  id: string;
  name: string;
  role: string;
  business_id: string;
  price?: number;
}

interface StaffData {
  id: string;
  name: string;
  business_id: string;
  role: string;
}

interface PriceData {
  staff_id: string;
  price: number;
}

interface Appointment {
  id: string;
  business_id: string;
  customer_name: string;
  customer_phone: string;
  staff_id: string;
  staff_name: string;
  service_id: string;
  service_name: string;
  start_time: string;
  end_time: string;
  status: string;
  duration: number;
  metadata?: {
    notes?: string;
    price?: number;
    invoice_url?: string; // הוסף שדה זה
  };
  // הוסף את השדות הבאים כדי לתמוך ב-relations מה-join
  customers?: {
    id: string;
    name: string;
    phone: string;
    // ...הוסף כאן שדות רלוונטיים נוספים אם צריך
  };
  services?: {
    id: string;
    name_he: string;
    duration: number;
    price?: number; 
    // ...הוסף כאן שדות רלוונטיים נוספים אם צריך
  };
  users?: {
    id: string;
    name: string;
    // ...הוסף כאן שדות רלוונטיים נוספים אם צריך
  };
}

interface AppointmentDetailsProps {
  appointment: Appointment;
  onClose: () => void;
  onUpdate: (updated?: Appointment) => void; // <-- עדכון כאן
}

interface StaffResponse {
  id: string;
  name: string;
  phone: string;
  email: string;
  role: string;
  business_id: string;
  created_at: string;
  updated_at: string;
  price: number;
}

interface Service {
  id: string;
  name_he: string;
  duration: number;
  price?: number;
}

interface PendingChanges {
  date?: string;
  time?: string;
  staff_id?: string;
  staff_name?: string;
  service_id?: string;
  service_name?: string;
  customer_phone?: string;
  duration?: number;
}

export function AppointmentDetails({ appointment, onClose, onUpdate }: AppointmentDetailsProps) {
  const { user } = useAuth();
  const { updateAppointmentStatus, loading: statusLoading, canEditAppointment } = useAppointmentStatus();
  const [loading, setLoading] = useState(false);
  const [canCharge, setCanCharge] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [logs, setLogs] = useState<any[]>([]);
  const [editingTime, setEditingTime] = useState(false);
  const [showDateSelector, setShowDateSelector] = useState(false);
  const [showStaffSelector, setShowStaffSelector] = useState(false);
  const [showEditConfirm, setShowEditConfirm] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<PendingChanges>({});
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [selectedTime, setSelectedTime] = useState(format(parseISO(appointment.start_time), 'HH:mm'));
  const [selectedDate, setSelectedDate] = useState(appointment.start_time.split('T')[0]);
  const [editingService, setEditingService] = useState(false);
  const [editingDuration, setEditingDuration] = useState(false);
  const [durationInput, setDurationInput] = useState(appointment.duration);
  const [selectedEndTime, setSelectedEndTime] = useState<string | null>(null);
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [showLoyaltyConfirm, setShowLoyaltyConfirm] = useState(false);
  const [hasPaymentTerminal, setHasPaymentTerminal] = useState(false);
  const [customerNoShowCount, setCustomerNoShowCount] = useState<number>(0);

  // הוסף סטייט חדש למידע הכי עדכני של התור
  const [currentAppointment, setCurrentAppointment] = useState<Appointment>(appointment);

  // הוסף סטייט לחשבונית מהטבלה payment_transactions
  const [invoiceUrl, setInvoiceUrl] = useState<string | null>(null);

  // הודעה לבר התחתון שהמודאל נפתח
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('ui:appointment-modal', { detail: { open: true } }));
    return () => {
      window.dispatchEvent(new CustomEvent('ui:appointment-modal', { detail: { open: false } }));
    };
  }, []);

  // בדיקה אם יש מסוף תשלום או חשבוניות
  useEffect(() => {
    const checkPaymentTerminal = async () => {
      try {
        const { data, error } = await supabase
          .from('payment_terminals')
          .select('id')
          .eq('business_id', appointment.business_id)
          .eq('is_active', true)
          .limit(1);

        if (!error && data && data.length > 0) {
          setHasPaymentTerminal(true);
        } else {
          setHasPaymentTerminal(false);
        }
      } catch (err) {
        setHasPaymentTerminal(false);
      }
    };

    checkPaymentTerminal();
  }, [appointment.business_id]);

  // טעינת מספר "לא הגיע" של הלקוח
  useEffect(() => {
    const fetchCustomerNoShowCount = async () => {
      try {
        const customerId = appointment.customers?.id;
        if (!customerId) return;

        const { data, error } = await supabase
          .from('customers')
          .select('no_show_count')
          .eq('id', customerId)
          .single();

        if (!error && data) {
          setCustomerNoShowCount(data.no_show_count || 0);
        }
      } catch (err) {
        console.error('Error fetching no_show_count:', err);
      }
    };

    fetchCustomerNoShowCount();
  }, [appointment.customers?.id, currentAppointment.status]);

  // הוסף סטייט לעריכת סכום
  const [editingPrice, setEditingPrice] = useState(false);
  const [priceInput, setPriceInput] = useState<number | ''>(
    typeof currentAppointment.metadata?.price === 'number'
      ? currentAppointment.metadata.price
      : ''
  );

  // שמור את מחיר השירות שמוצג ליד השירות במשתנה priceForCharge
  const priceForCharge =
    typeof currentAppointment.metadata?.price === 'number'
      ? currentAppointment.metadata.price
      : typeof currentAppointment.services?.price === 'number'
      ? currentAppointment.services.price
      : 0;

  // האם התור הושלם? (סטטוס completed)
  const isCompleted = currentAppointment.status === 'completed';

  // פונקציה לקבלת שם המשתמש מטבלת users
  const getUserName = async (): Promise<string> => {
    if (!user?.id) return 'משתמש';

    try {
      const { data, error } = await supabase
        .from('users')
        .select('name')
        .eq('id', user.id)
        .single();

      if (!error && data?.name) {
        return data.name;
      }
    } catch (err) {
      console.error('Error fetching user name:', err);
    }

    // fallback לשם מ-metadata או אימייל
    return user.user_metadata?.name || user.email || 'משתמש';
  };

  // בכל פעם שהתור מתחלף (modal חדש), אתחל את currentAppointment
  useEffect(() => {
    setCurrentAppointment(appointment);
    setSelectedTime(format(parseISO(appointment.start_time), 'HH:mm'));
    setSelectedDate(appointment.start_time.split('T')[0]);

    // סדר עדיפויות: staff_services.duration > services.duration > appointment.duration
    async function fetchInitialDuration() {
      let duration = 0;
      // נסה למשוך מהצוות
      if (appointment.staff_id && appointment.service_id) {
        const { data: staffService, error } = await supabase
          .from('staff_services')
          .select('duration')
          .eq('staff_id', appointment.staff_id)
          .eq('service_id', appointment.service_id)
          .single();
        // אם duration הוא מחרוזת זמן (למשל "00:40:00" או "00:00:40"), המר למספר דקות
        if (!error && staffService && staffService.duration) {
          let raw = staffService.duration;
          if (typeof raw === 'string') {
            // תומך גם בפורמט "00:40:00" וגם "00:00:40"
            const parts = raw.split(':').map(Number);
            // אם יש 3 חלקים: HH:mm:ss
            if (parts.length === 3) {
              duration = parts[0] * 60 + parts[1]; // שעות לדקות + דקות
              // אם יש רק שניות, תתעלם
            } else if (parts.length === 2) {
              duration = parts[0] * 60 + parts[1];
            } else if (parts.length === 1) {
              duration = parts[0];
            }
            // הגנה: אם duration קטן מ-1, קח את ההפרש בין שעת התחלה לשעת סיום
            if (duration < 1 && appointment.start_time && appointment.end_time) {
              const start = parseISO(appointment.start_time);
              const end = parseISO(appointment.end_time);
              duration = differenceInMinutes(end, start);
            }
          } else if (typeof raw === 'number') {
            duration = raw;
          }
        }
      }
      // אם לא קיים אצל הצוות, נסה מהשירות
      if (!duration && appointment.services?.duration) {
        let raw = appointment.services.duration;
        if (typeof raw === 'string') {
          const parts = raw.split(':').map(Number);
          if (parts.length === 3) {
            duration = parts[0] * 60 + parts[1];
          } else if (parts.length === 2) {
            duration = parts[0] * 60 + parts[1];
          } else if (parts.length === 1) {
            duration = parts[0];
          }
          if (duration < 1 && appointment.start_time && appointment.end_time) {
            const start = parseISO(appointment.start_time);
            const end = parseISO(appointment.end_time);
            duration = differenceInMinutes(end, start);
          }
        } else if (typeof raw === 'number') {
          duration = raw;
        }
      }
      // אם לא קיים גם אצל השירות, קח מהתור עצמו
      if (!duration && typeof appointment.duration === 'number' && appointment.duration > 0) {
        duration = appointment.duration;
      }
      // אם עדיין לא קיים, קח את ההפרש בין שעת התחלה לשעת סיום
      if (!duration && appointment.start_time && appointment.end_time) {
        const start = parseISO(appointment.start_time);
        const end = parseISO(appointment.end_time);
        duration = differenceInMinutes(end, start);
      }
      setDurationInput(duration); // <-- כאן תעדכן את ה־durationInput לערך הנבחר
    }
    fetchInitialDuration();

    setSelectedEndTime(null);
    // אפשר גם לאפס pendingChanges כאן אם אתה רוצה שהשדות יתאפסו אחרי עדכון
    // setPendingChanges({});
  }, [appointment]);

  // עדכן את priceInput בכל פעם שהתור מתחלף
  useEffect(() => {
    setPriceInput(
      typeof appointment.metadata?.price === 'number'
        ? appointment.metadata.price
        : ''
    );
  }, [appointment]);

  useEffect(() => {
    async function fetchInvoiceUrl() {
      if (!appointment.id) {
        setInvoiceUrl(null);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('payment_transactions')
          .select('invoice_link')
          .eq('appointment_id', appointment.id)
          .maybeSingle();

        if (!error && data?.invoice_link) {
          setInvoiceUrl(data.invoice_link);
        }
      } catch (err) {

      }
    }

    fetchInvoiceUrl();
  }, [appointment.id]);

  useEffect(() => {
    async function checkCanCharge() {
    if (!appointment.business_id) {
      return;
    }

    try {
      const { data: subscriptionData, error } = await supabase
        .from('subscriptions')
        .select('plan_id')
        .eq('business_id', appointment.business_id)
        .maybeSingle();

      if (error) {

        setCanCharge(false);
        return;
      }

      if (!subscriptionData) {
        setCanCharge(false);
        return;
      }

      const basicPlanId = '686adae1-c8ab-46a8-aea4-d5a78e431723';
      setCanCharge(subscriptionData?.plan_id !== basicPlanId);
    } catch (err) {

      setCanCharge(false);
    }
  }

  checkCanCharge();
}, [appointment.business_id]);

  // Calculate appointment duration in minutes
  const duration = useMemo(() => {
    const start = parseISO(currentAppointment.start_time);
    const end = parseISO(currentAppointment.end_time);
    return differenceInMinutes(end, start);
  }, [currentAppointment.start_time, currentAppointment.end_time]);

  // Load staff members
  useEffect(() => {
    const fetchStaffMembers = async () => {
      try {
        const { data: staffData, error: staffError } = await supabase
          .from('users')
          .select('id, name, business_id, role')
          .eq('business_id', appointment.business_id)
          .in('role', ['staff', 'admin']);

        if (staffError) {

          toast.error('שגיאה בטעינת אנשי הצוות');
          return;
        }

        const { data: pricesData, error: pricesError } = await supabase
          .from('staff_services')
          .select('staff_id, price')
          .eq('service_id', appointment.service_id);

        if (pricesError) {

        }

        const staffWithPrices: StaffMember[] = (staffData as StaffData[]).map((staff) => {
          const priceInfo = (pricesData as PriceData[])?.find(p => p.staff_id === staff.id);
          return {
            ...staff,
            price: priceInfo?.price
          };
        });

        const sortedStaff = staffWithPrices.sort((a, b) => a.name.localeCompare(b.name));
        setStaffMembers(sortedStaff);
      } catch (error) {

        toast.error('שגיאה בטעינת אנשי הצוות');
      }
    };

    fetchStaffMembers();
  }, [appointment.business_id, appointment.service_id]);

  // Load services for the business
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const { data, error } = await supabase
          .from('services')
          .select('id, name_he, duration, price')
          .eq('business_id', appointment.business_id);

        if (error) throw error;
        setServices(data || []);
      } catch (error) {
        toast.error('שגיאה בטעינת השירותים');
      }
    };
    fetchServices();
  }, [appointment.business_id]);

  // הוסף סטייט לשעות פנויות ולטעינה
  const [availableTimes, setAvailableTimes] = useState<string[]>([]);
  const [availableTimesLoading, setAvailableTimesLoading] = useState(false);

  // סטייט לשעות פעילות העסק
  const [businessHours, setBusinessHours] = useState<any>(null);

  // טען שעות פעילות מהטבלה business_hours (regular_hours)
  useEffect(() => {
    async function fetchBusinessHours() {
      const { data } = await supabase
        .from('business_hours')
        .select('regular_hours')
        .eq('business_id', appointment.business_id)
        .single();
      if (data?.regular_hours) {
        setBusinessHours(data.regular_hours);
      } else {
        setBusinessHours(null);
      }
    }
    fetchBusinessHours();
  }, [appointment.business_id]);

  // שליפת שעות פנויות לפי שעות פעילות העסק + בדיקת תורים קיימים
  useEffect(() => {
    async function fetchAvailableTimes() {
      setAvailableTimesLoading(true);
      try {
        if (!businessHours || !selectedDate || !currentAppointment.staff_id || !durationInput) {
          setAvailableTimes([]);
          setAvailableTimesLoading(false);
          return;
        }
        // קבע יום בשבוע
        if (typeof selectedDate !== 'string' || !selectedDate.includes('-')) {
          setAvailableTimes([]);
          setAvailableTimesLoading(false);
          return;
        }
        const dayOfWeek = new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
        const hours = businessHours?.[dayOfWeek];
        if (!hours || !hours.isActive) {
          setAvailableTimes([]);
          setAvailableTimesLoading(false);
          return;
        }
        // טווח שעות עבודה
        const startHour = Number(hours.startTime.split(':')[0]);
        const startMinute = Number(hours.startTime.split(':')[1]);
        const endHour = Number(hours.endTime.split(':')[0]);
        const endMinute = Number(hours.endTime.split(':')[1]);

        // שלוף תורים קיימים לאותו צוות באותו יום
        const startOfDay = selectedDate + 'T00:00:00';
        const endOfDay = selectedDate + 'T23:59:59';
        const { data: appointments, error: appointmentsError } = await supabase
          .from('appointments')
          .select('start_time, end_time')
          .eq('staff_id', currentAppointment.staff_id)
          .gte('start_time', startOfDay)
          .lte('end_time', endOfDay)
          .or('status.neq.canceled,status.neq.no_show'); // במקום not.in
        if (appointmentsError) {

        }

        // צור מערך של כל הזמנים בתבנית HH:mm ב-5 דקות בטווח הפעיל בלבד
        const times: string[] = [];
        let h = startHour, m = startMinute;
        while (h < endHour || (h === endHour && m < endMinute)) {
          times.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
          m += 5;
          if (m >= 60) {
            m = 0;
            h += 1;
          }
        }

        // סינון שעות תפוסות לפי תורים קיימים
        const isFree = (time: string) => {
          const [hh, mm] = time.split(':').map(Number);
          const start = new Date(selectedDate + `T${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:00`);
          const end = new Date(start.getTime() + durationInput * 60000);
          return !appointments?.some(app => {
            const appStart = parseISO(app.start_time);
            const appEnd = parseISO(app.end_time);
            // בדוק חפיפה מלאה או חלקית
            return (
              (start < appEnd && end > appStart)
            );
          });
        };

        setAvailableTimes(times.filter(isFree));
      } catch {
        setAvailableTimes([]);
      } finally {
        setAvailableTimesLoading(false);
      }
    }
    if (editingTime) {
      fetchAvailableTimes();
    }
    // eslint-disable-next-line
  }, [editingTime, businessHours, selectedDate, currentAppointment.staff_id, durationInput]);

  const handleSaveChanges = async () => {
    try {
      setLoading(true);

      // --- שינוי שעת התחלה/סיום ---
      if (pendingChanges.date || pendingChanges.time || pendingChanges.end_time) {
        const originalStart = parseISO(appointment.start_time);

        // קבע שעת התחלה חדשה
        let newStartDate = originalStart;
        if (pendingChanges.date || pendingChanges.time) {
          const year = pendingChanges.date
            ? Number(pendingChanges.date.split('-')[0])
            : originalStart.getUTCFullYear();
          const month = pendingChanges.date
            ? Number(pendingChanges.date.split('-')[1]) - 1
            : originalStart.getUTCMonth();
          const day = pendingChanges.date
            ? Number(pendingChanges.date.split('-')[2])
            : originalStart.getUTCDate();
          const hours = pendingChanges.time
            ? Number(pendingChanges.time.split(':')[0]) - 2
            : originalStart.getUTCHours();
          const minutes = pendingChanges.time
            ? Number(pendingChanges.time.split(':')[1])
            : originalStart.getUTCMinutes();
          // הגנה: אם אחד מהערכים NaN, אל תבנה תאריך לא חוקי
          if (
            !isNaN(year) && !isNaN(month) && !isNaN(day) &&
            !isNaN(hours) && !isNaN(minutes)
          ) {
            newStartDate = new Date(Date.UTC(year, month, day, hours, minutes));
          } else {
            toast.error('שעת התחלה או תאריך לא תקינים');
            setLoading(false);
            return;
          }
        }

        // קבע שעת סיום חדשה (אם נבחרה והיא תקינה), אחרת שמור את שעת הסיום המקורית
        let newEndDate: Date;
        const endTimeStr = selectedEndTime;
        if (
          endTimeStr &&
          typeof endTimeStr === 'string'
          && /^\d{2}:\d{2}$/.test(endTimeStr)
        ) {
          const [endHours, endMinutes] = endTimeStr.split(':').map(Number);
          if (
            endHours !== undefined &&
            endMinutes !== undefined &&
            !isNaN(endHours) &&
            !isNaN(endMinutes)
          ) {
            newEndDate = new Date(Date.UTC(
              newStartDate.getUTCFullYear(),
              newStartDate.getUTCMonth(),
              newStartDate.getUTCDate(),
              endHours - 2,
              endMinutes
            ));
          } else {
            toast.error('שעת סיום לא תקינה');
            setLoading(false);
            return;
          }
        } else if (endTimeStr === '' || endTimeStr === null) {
          // אם המשתמש לא בחר שעת סיום, שמור את שעת הסיום המקורית (אל תחשב אוטומטית)
          newEndDate = parseISO(appointment.end_time);
        } else {
          toast.error('שעת סיום לא תקינה');
          setLoading(false);
          return;
        }

        // הגנה: אם newEndDate לא חוקי
        if (isNaN(newEndDate.getTime())) {
          toast.error('שעת סיום לא תקינה');
          setLoading(false);
          return;
        }

        // עדכן גם את משך התור לפי שעת סיום חדשה
        const newDuration = Math.round((newEndDate.getTime() - newStartDate.getTime()) / 60000);

        const formatUTC = (date: Date) => {
          return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')} ${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}:00+0000`;
        };

        const { error: dateError } = await supabase
          .from('appointments')
          .update({
            start_time: formatUTC(newStartDate),
            end_time: formatUTC(newEndDate),
            duration: newDuration
          })
          .eq('id', appointment.id);

        if (dateError) throw dateError;

        // Log the changes separately
        if (pendingChanges.time) {
          const userName = await getUserName();
          const { error: timeLogError } = await supabase
            .from('appointment_logs')
            .insert({
              appointment_id: appointment.id,
              user_id: user?.id,
              action: 'time_change',
              details: {
                timestamp: new Date().toISOString(),
                user_name: userName,
                old_time: format(originalStart, 'HH:mm', { locale: he }),
                new_time: pendingChanges.time,
                reason: 'שינוי זמן תור'
              }
            });

          if (timeLogError) throw timeLogError;
        }

        if (pendingChanges.date) {
          const userName = await getUserName();
          const { error: dateLogError } = await supabase
            .from('appointment_logs')
            .insert({
              appointment_id: appointment.id,
              user_id: user?.id,
              action: 'time_change',
              details: {
                reason: 'שינוי זמן תור',
                new_time: format(parseISO(pendingChanges.date), 'd', { locale: he }),
                old_time: format(originalStart, 'd', { locale: he }),
                timestamp: new Date().toISOString(),
                user_name: userName
              }
            });

          if (dateLogError) throw dateLogError;
        }
      }

      if (pendingChanges.staff_id) {
        const { error: staffError } = await supabase
          .from('appointments')
          .update({
            staff_id: pendingChanges.staff_id
          })
          .eq('id', appointment.id);

        if (staffError) throw staffError;

        // Add log for staff change
        const userName = await getUserName();
        const { error: logError } = await supabase
          .from('appointment_logs')
          .insert({
            appointment_id: appointment.id,
            user_id: user?.id,
            action: 'staff_change',
            details: {
              timestamp: new Date().toISOString(),
              user_name: userName,
              old_staff: appointment.staff_name,
              new_staff: pendingChanges.staff_name,
              reason: 'שינוי איש צוות'
            }
          });

        if (logError) throw logError;
      }

      // Service change
      if (pendingChanges.service_id) {
        // ודא ש-duration הוא מספר ולא מחרוזת
        let durationValue = pendingChanges.duration;
        if (typeof durationValue === 'string') {
          const parts = durationValue.split(':').map(Number);
          durationValue = parts[0] * 60 + (parts[1] || 0);
        }
        const { error: serviceError } = await supabase
          .from('appointments')
          .update({
            service_id: pendingChanges.service_id,
            // עדכן גם את משך השירות אם יש
            ...(durationValue ? { duration: durationValue } : {})
          })
          .eq('id', appointment.id);

        if (serviceError) throw serviceError;

        // Log
        const userName = await getUserName();
        await supabase.from('appointment_logs').insert({
          appointment_id: appointment.id,
          user_id: user?.id,
          action: 'service_change',
          details: {
            timestamp: new Date().toISOString(),
            user_name: userName,
            old_service: appointment.services?.name_he || appointment.service_name,
            new_service: pendingChanges.service_name,
            reason: 'שינוי שירות'
          }
        });
      }

      // Phone change
      if (
        pendingChanges.customer_phone &&
        pendingChanges.customer_phone !== (appointment.customers?.phone || appointment.customer_phone) &&
        appointment.customers?.id // ודא שיש לקוח
      ) {
        const { error: phoneError } = await supabase
          .from('customers')
          .update({ phone: pendingChanges.customer_phone })
          .eq('id', appointment.customers.id);

        if (phoneError) throw phoneError;

        // Log
        const userName = await getUserName();
        await supabase.from('appointment_logs').insert({
          appointment_id: appointment.id,
          user_id: user?.id,
          action: 'phone_change',
          details: {
            timestamp: new Date().toISOString(),
            user_name: userName,
            old_phone: appointment.customers?.phone || appointment.customer_phone,
            new_phone: pendingChanges.customer_phone,
            reason: 'שינוי טלפון'
          }
        });
      }

      // Duration change
      if (pendingChanges.duration && pendingChanges.duration !== appointment.duration) {
        // Update appointment duration and end_time
        const start = parseISO(appointment.start_time);
        const newEnd = new Date(start.getTime() + pendingChanges.duration * 60000);
        const formatUTC = (date: Date) => {
          return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')} ${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}:00+0000`;
        };
        const { error: durationError } = await supabase
          .from('appointments')
          .update({
            duration: pendingChanges.duration, // <-- ודא שזה מספר, לא מחרוזת!
            end_time: formatUTC(newEnd)
          })
          .eq('id', appointment.id);

        if (durationError) throw durationError;

        // Log
        const userName = await getUserName();
        await supabase.from('appointment_logs').insert({
          appointment_id: appointment.id,
          user_id: user?.id,
          action: 'duration_change',
          details: {
            timestamp: new Date().toISOString(),
            user_name: userName,
            old_duration: appointment.duration,
            new_duration: pendingChanges.duration,
            reason: 'שינוי משך שירות'
          }
        });
      }

      toast.success('התור עודכן בהצלחה');

      // שלוף מחדש את התור מה-DB כולל joins
      const { data: updated, error: fetchError } = await supabase
        .from('appointments')
        .select('*, customers(*), services(*)') // כאן services(*) כולל גם duration
        .eq('id', appointment.id)
        .single();

      if (!fetchError && updated) {
        setCurrentAppointment(updated);
        // עדכן גם את השדות התלויים (inputs) למידע הכי עדכני
        setSelectedTime(format(parseISO(updated.start_time), 'HH:mm'));
        setSelectedDate(updated.start_time.split('T')[0]);
        setDurationInput(updated.duration);
        onUpdate(updated); // <-- העבר את התור החדש ל-parent
      } else {
        onUpdate(); // fallback
      }

      setShowEditConfirm(false);
      setPendingChanges({});
      setSelectedEndTime(null); // אפס את שעת הסיום שנבחרה
    } catch (error) {

      toast.error('שגיאה בעדכון התור');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    const success = await updateAppointmentStatus({
      appointmentId: appointment.id,
      newStatus: 'confirmed',
      reason: 'אישור ידני'
    });
    
    if (success) {
      onUpdate();
      onClose();
    }
  };

  const handleMarkCompleted = async () => {
    const success = await updateAppointmentStatus({
      appointmentId: appointment.id,
      newStatus: 'completed',
      reason: 'סימון ידני כהושלם'
    });
    
    if (success) {
      onUpdate();
      onClose();
    }
  };

  const handleMarkNoShow = async () => {
    const success = await updateAppointmentStatus({
      appointmentId: appointment.id,
      newStatus: 'no_show',
      reason: 'סימון ידני כלא הגיע'
    });
    
    if (success) {
      onUpdate();
      onClose();
    }
  };

  const handleMarkBooked = async () => {
    const success = await updateAppointmentStatus({
      appointmentId: appointment.id,
      newStatus: 'booked',
      reason: 'החזרה ידנית למצב המתנה'
    });
    
    if (success) {
      onUpdate();
      onClose();
    }
  };

  const handleCancel = async () => {
    const success = await updateAppointmentStatus({
      appointmentId: appointment.id,
      newStatus: 'canceled',
      reason: 'ביטול ידני'
    });

    if (success) {
      onUpdate();
      onClose();
    }
    setShowCancelConfirm(false);
  };

  const handleChargeSuccess = async () => {
    // עדכן סטטוס ל-completed
    await updateAppointmentStatus({
      appointmentId: appointment.id,
      newStatus: 'completed',
      reason: 'חיוב לקוח דרך תור'
    });
    setShowChargeModal(false);
    onUpdate();
    onClose();
  };

  const getStatusText = (status: string | null) => {
    switch (status) {
      case 'booked':
        return 'ממתין לאישור';
      case 'confirmed':
        return 'מאושר';
      case 'completed':
        return 'הושלם';
      case 'no_show':
        return 'לא הגיע';
      case 'canceled':
        return 'בוטל';
      default:
        return status;
    }
  };

  // מחק את כל ה-useEffect שמאזינים ל-[appointment] או [appointment.id] או שמבצעים polling

  // הוסף useEffect שמאזין ל-currentAppointment.id ומבצע subscribe ל-realtime של Supabase (אם יש לך Supabase v2 ומעלה)
  useEffect(() => {
    // רענון ראשוני
    async function fetchAppointmentAndLogs() {
      const { data: appointmentData } = await supabase
        .from('appointments')
        .select('*, customers(*), services(*)')
        .eq('id', appointment.id)
        .single();
      if (appointmentData) {
        setCurrentAppointment(appointmentData);
        setSelectedTime(format(parseISO(appointmentData.start_time), 'HH:mm'));
        setSelectedDate(appointmentData.start_time.split('T')[0]);
        setSelectedEndTime(null);
        // אל תעדכן כאן את durationInput!
      }
      const { data: logsData } = await supabase
        .from('appointment_logs')
        .select('*')
        .eq('appointment_id', appointment.id)
        .order('created_at', { ascending: false });
      if (logsData) setLogs(logsData);
    }
    fetchAppointmentAndLogs();

    // Supabase realtime subscription (אם יש לך Supabase Realtime מופעל)
    const channel = supabase
      .channel('realtime-appointments-' + appointment.id)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'appointments', filter: `id=eq.${appointment.id}` },
        (payload) => {
          // כל שינוי בתור - שלוף מחדש
          fetchAppointmentAndLogs();
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'appointment_logs', filter: `appointment_id=eq.${appointment.id}` },
        (payload) => {
          // כל שינוי בלוגים - שלוף מחדש
          fetchAppointmentAndLogs();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [appointment.id]);

  // הוסף סטייטים חדשים להטבות ולחישוב מחיר סופי
  const [benefits, setBenefits] = useState<{
    basePrice: number;
    loyaltyDiscount: number;
    finalPrice: number;
    loyaltyLevel: string;
    selectedBenefits: {
      loyaltyDiscount: boolean;
    };
  } | null>(null);
  const [benefitsLoading, setBenefitsLoading] = useState(false);
  const [benefitsError, setBenefitsError] = useState<string | null>(null);

  useEffect(() => {
    const calculateBenefits = async () => {
      if (!currentAppointment.customers?.id || !currentAppointment.service_id || !currentAppointment.staff_id) {
        setBenefits(null);
        return;
      }
      try {
        setBenefitsLoading(true);
        // Get staff service price
        const { data: staffService } = await supabase
          .from('staff_services')
          .select('price')
          .eq('staff_id', currentAppointment.staff_id)
          .eq('service_id', currentAppointment.service_id)
          .single();

        const basePrice = staffService?.price || currentAppointment.services?.price || 0;

        // Get customer's loyalty level
        const { data: customerData } = await supabase
          .from('customers')
          .select('loyalty_level')
          .eq('id', currentAppointment.customers.id)
          .single();

        if (!customerData) {
          setBenefits(null);
          return;
        }

        // Get loyalty settings
        const { data: businessData } = await supabase
          .from('businesses')
          .select('settings->loyalty')
          .eq('id', currentAppointment.business_id)
          .single();

        if (!businessData?.loyalty) {
          setBenefits(null);
          return;
        }

        const loyaltyLevel = customerData.loyalty_level;
        const loyaltyBenefits = businessData.loyalty.levels?.[loyaltyLevel]?.benefits;

        let loyaltyDiscount = 0;
        let finalPrice = basePrice;

        // בדוק אם ההנחה כבר הופעלה בעבר (שמור ב-metadata)
        const isDiscountApplied = currentAppointment.metadata?.loyalty_discount_applied === true;
        let selectedBenefits = { loyaltyDiscount: isDiscountApplied };

        if (loyaltyBenefits?.services_discount) {
          loyaltyDiscount = (basePrice * loyaltyBenefits.services_discount) / 100;

          // אם ההנחה כבר הופעלה בעבר, החל אותה גם עכשיו
          if (isDiscountApplied) {
            finalPrice = basePrice - loyaltyDiscount;
          }
        }

        setBenefits({
          basePrice,
          loyaltyDiscount,
          finalPrice,
          loyaltyLevel,
          selectedBenefits
        });
      } catch (error) {
        setBenefitsError('שגיאה בחישוב ההטבות');
        setBenefits(null);
      } finally {
        setBenefitsLoading(false);
      }
    };

    calculateBenefits();
  }, [currentAppointment.customers?.id, currentAppointment.service_id, currentAppointment.staff_id, currentAppointment.business_id, currentAppointment.services?.price]);
  
  // פונקציה להפעלת/ביטול הנחה ע"י המשתמש
  const toggleLoyaltyDiscount = async () => {
    if (!benefits) return;
    const newSelected = !benefits.selectedBenefits.loyaltyDiscount;
    let finalPrice = benefits.basePrice;
    let newPrice = benefits.basePrice;
    if (newSelected) {
      finalPrice -= benefits.loyaltyDiscount;
      newPrice = finalPrice;
    }
    setBenefits({
      ...benefits,
      selectedBenefits: { loyaltyDiscount: newSelected },
      finalPrice
    });

    try {
      const updatedMetadata = {
        ...currentAppointment.metadata,
        price: newPrice,
        loyalty_discount_applied: newSelected,
        loyalty_discount_amount: newSelected ? benefits.loyaltyDiscount : 0
      };

      await supabase
        .from('appointments')
        .update({ metadata: updatedMetadata })
        .eq('id', currentAppointment.id);

      const updatedAppointment = {
        ...currentAppointment,
        metadata: updatedMetadata
      };

      setCurrentAppointment(updatedAppointment);

      // עדכן את הקומפוננטה האב
      onUpdate(updatedAppointment);

      // רשום לוג
      const userName = await getUserName();
      await supabase.from('appointment_logs').insert({
        appointment_id: currentAppointment.id,
        user_id: user?.id,
        action: 'loyalty_discount_change',
        details: {
          timestamp: new Date().toISOString(),
          user_name: userName,
          discount_applied: newSelected,
          discount_amount: newSelected ? benefits.loyaltyDiscount : 0,
          original_price: benefits.basePrice,
          final_price: newPrice,
          loyalty_level: benefits.loyaltyLevel,
          reason: newSelected ? 'הפעלת הנחת נאמנות' : 'ביטול הנחת נאמנות'
        }
      });

      toast.success(newSelected ? 'הנחת נאמנות הופעלה' : 'הנחת נאמנות בוטלה');
    } catch (e) {
      console.error('Error toggling loyalty discount:', e);
      toast.error('שגיאה בשמירת ההנחה');
    }
  };

  // הוסף סטייט loyaltyEnabled בתחילת הקומפוננטה
  const [loyaltyEnabled, setLoyaltyEnabled] = useState<boolean>(false);

  // קבע זמינות נאמנות רק לפי הגדרת העסק (לא לפי חבילה)
  useEffect(() => {
    async function fetchLoyaltyEnabled() {
      try {
        const { data: businessData } = await supabase
          .from('businesses')
          .select('settings')
          .eq('id', appointment.business_id)
          .single();
        const enabled = (
          businessData?.settings?.loyalty &&
          typeof businessData.settings.loyalty.enabled === 'boolean'
        )
          ? businessData.settings.loyalty.enabled
          : (typeof businessData?.settings?.loyalty_enabled === 'boolean'
            ? businessData.settings.loyalty_enabled
            : false);
        setLoyaltyEnabled(!!enabled);
      } catch {
        setLoyaltyEnabled(false);
      }
    }
    fetchLoyaltyEnabled();
  }, [appointment.business_id]);

  // הוסף useEffect שמעדכן את סכום ברירת המחדל (price) עבור חיוב לקוח
  useEffect(() => {
    // נסה לשלוף מחיר מהתור (העדפה: metadata.price > services.price > 0)
    let price =
      typeof appointment.metadata?.price === 'number'
        ? appointment.metadata.price
        : typeof appointment.services?.price === 'number'
        ? appointment.services.price
        : 0;
    setPriceInput(price);
  }, [appointment]);

  // הגדר את canEdit בתחילת הקומפוננטה
  const canEdit = canEditAppointment(currentAppointment.status);

  // הוסף את הפונקציה לבדיקת יום פעילות העסק
  function isBusinessDayOpen(dateStr: string) {
    if (!businessHours) return false;
    const special = specialDates.find(d => d.date === dateStr);
    if (special) {
      if (special.is_closed === true) return false;
      if (special.is_closed === false) return true;
      if (special.start_time || special.end_time) return true;
    }
    // פרש את dateStr כ-local date (לא UTC)
    const [y, m, d] = dateStr.split('-').map(Number);
    const localDate = new Date(y, m - 1, d);
    const dayOfWeek = localDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const hours = businessHours?.[dayOfWeek];
    return !!(hours && (hours.isActive || hours.is_active));
  }

  // הוסף סטייט לימים מיוחדים מה-business_hours.special_dates
  const [specialDates, setSpecialDates] = useState<
    { id: string; date: string; note?: string; is_closed?: boolean; start_time?: string; end_time?: string }[]
  >([]);

  // טען את special_dates מה-business_hours
  useEffect(() => {
    async function fetchSpecialDates() {
      const { data, error } = await supabase
        .from('business_hours')
        .select('special_dates')
        .eq('business_id', appointment.business_id)
        .single();

      if (!error && data && Array.isArray(data.special_dates)) {
        setSpecialDates(data.special_dates);
      } else if (!error && data && typeof data.special_dates === 'string') {
        // אם זה מחרוזת JSON
        try {
          setSpecialDates(JSON.parse(data.special_dates));
        } catch {
          setSpecialDates([]);
        }
      } else {
        setSpecialDates([]);
      }
    }
    fetchSpecialDates();
  }, [appointment.business_id]);

  // עדכן את הפונקציה isBusinessDayOpen כך שתחסום רק אם כל היום סגור (is_closed=true או אין שעות פעילות בכלל)
// הפסקות לא חוסמות את כל היום, אלא רק שעות מסוימות
function isBusinessDayOpen(dateStr: string) {
  if (!businessHours) return false;
  const special = specialDates.find(d => d.date === dateStr);
  if (special) {
    if (special.is_closed === true) return false;
    if (special.is_closed === false) return true;
    if (special.start_time || special.end_time) return true;
  }
  // פרש את dateStr כ-local date (לא UTC)
  const [y, m, d] = dateStr.split('-').map(Number);
  const localDate = new Date(y, m - 1, d);
  const dayOfWeek = localDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
  const hours = businessHours?.[dayOfWeek];
  return !!(hours && (hours.isActive || hours.is_active));
}

// ברשימת השעות (בחירת שעת התחלה), חסום רק שעות שהן לא בטווח שעות הפעילות או שנמצאות בהפסקה
// (הקוד שלך כבר עושה זאת נכון: רק שעות זמינות מוצגות כפעילות, אחרות חסומות)
// ...existing code...

  // בורר ימים - עיצוב מודרני: כפתור פתוח = רקע לבן, border/hover, טקסט כהה; כפתור חסום = רקע אפור, border אפור, טקסט דהוי; אין נקודה ירוקה/אפור, רק עיצוב הכפתור
  // יום נבחר: border עבה/צבע, רקע highlight, טקסט לבן

  // ב-render של modal/overlay (הקומפוננטה הראשית):
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]"
      style={{ overflowY: 'auto' }}
      onClick={(e) => {
        // אם המשתמש לחץ מחוץ למודל (כלומר על הרקע), אפס מצב עריכה של שעה ושעת סיום
        if (e.target === e.currentTarget) {
          setEditingTime(false);
          setSelectedEndTime(null);
          setShowEditConfirm(false);
          setPendingChanges({});
          onClose();
        }
      }}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-lg flex flex-col"
        style={{
          height: '70vh',
          maxHeight: '70vh'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-5 pt-4 pb-5 border-b border-gray-100">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 left-4 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>

          {/* Customer Name & Status */}
          <div className="text-center pt-2">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              {currentAppointment.customers?.name || currentAppointment.customer_name}
            </h2>
            <div className="flex items-center justify-center gap-2">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                currentAppointment.status === 'booked' ? 'bg-amber-50 text-amber-700' :
                currentAppointment.status === 'confirmed' ? 'bg-blue-50 text-blue-700' :
                currentAppointment.status === 'completed' ? 'bg-emerald-50 text-emerald-700' :
                currentAppointment.status === 'no_show' ? 'bg-orange-50 text-orange-700' :
                'bg-red-50 text-red-700'
              }`}>
                {getStatusText(currentAppointment.status)}
              </span>
              {customerNoShowCount > 0 && (
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                  customerNoShowCount >= 3 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                }`}>
                  <AlertTriangle className="h-3.5 w-3.5" />
                  <span>{customerNoShowCount} לא הגיע</span>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Main Info Grid - Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
        <div className="px-5 py-5">
          <div className="grid grid-cols-2 gap-3">
            {/* Date */}
            <div
              className={`flex items-center gap-2.5 p-3 bg-gray-50 rounded-lg transition-colors ${
                !canEdit ? 'opacity-60' : 'active:bg-gray-100 cursor-pointer'
              }`}
              onClick={() => !loading && canEdit && setShowDateSelector(true)}
            >
              <Calendar className="h-4 w-4 text-gray-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 mb-0.5">תאריך</p>
                <p className="text-sm font-medium text-gray-900 truncate">
                  {format(parseISO(pendingChanges.date || currentAppointment.start_time), 'd/M', { locale: he })}
                </p>
              </div>
            </div>

            {/* Time */}
            <div className="flex items-center gap-2.5 p-3 bg-gray-50 rounded-lg">
              <Clock className="h-4 w-4 text-gray-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 mb-0.5">שעה</p>
                <div className="flex items-center gap-1 text-sm font-medium text-gray-900">
                  {(() => {
                      const InlineTimePicker = ({
                        value,
                        onChange,
                        isEditing,
                        setIsEditing,
                        availableTimes
                      }: {
                        value: string;
                        onChange: (v: string) => void;
                        isEditing: boolean;
                        setIsEditing: (v: boolean) => void;
                        availableTimes: string[];
                      }) => {
                        const scrollRef = useRef<HTMLDivElement>(null);
                        const itemHeight = 32;
                        const [localValue, setLocalValue] = useState(value);

                        useEffect(() => {
                          if (scrollRef.current && localValue) {
                            const index = availableTimes.indexOf(localValue);
                            if (index >= 0) {
                              scrollRef.current.scrollTop = index * itemHeight;
                            }
                          }
                        }, [isEditing]);

                        const handleScroll = () => {
                          if (!scrollRef.current) return;
                          const scrollTop = scrollRef.current.scrollTop;
                          const index = Math.round(scrollTop / itemHeight);
                          const selected = availableTimes[index];
                          if (selected) {
                            setLocalValue(selected);
                          }
                        };

                        if (!isEditing) {
                          return (
                            <span
                              onClick={() => canEdit && setIsEditing(true)}
                              className={canEdit ? "cursor-pointer hover:text-blue-600 transition-colors px-2 py-1 rounded bg-blue-50 border border-blue-200" : "px-2 py-1"}
                            >
                              {value}
                            </span>
                          );
                        }

                        return (
                          <div className="relative">
                            <div className="absolute top-0 left-0 z-50 flex flex-col items-center gap-2 bg-white border-2 border-blue-500 rounded-xl shadow-2xl p-3" style={{ minWidth: '140px' }}>
                              <div className="relative bg-gradient-to-b from-blue-50 to-white rounded-lg overflow-hidden border border-blue-200 w-full" style={{ height: itemHeight * 3 }}>
                                <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-10 bg-blue-500/10 border-y-2 border-blue-500/40 pointer-events-none z-10 rounded"></div>
                                <div
                                  ref={scrollRef}
                                  onScroll={handleScroll}
                                  className="h-full overflow-y-auto snap-y snap-mandatory scrollbar-hide"
                                  style={{
                                    scrollSnapType: 'y mandatory',
                                    WebkitOverflowScrolling: 'touch',
                                    scrollbarWidth: 'none',
                                    msOverflowStyle: 'none',
                                  }}
                                >
                                  <div style={{ height: itemHeight }}></div>
                                  {availableTimes.map((time) => {
                                    const isSelected = time === localValue;
                                    return (
                                      <div
                                        key={time}
                                        className="flex items-center justify-center snap-start transition-all duration-150"
                                        style={{
                                          height: itemHeight,
                                          fontSize: isSelected ? '18px' : '14px',
                                          fontWeight: isSelected ? 700 : 500,
                                          color: isSelected ? '#2563eb' : '#9ca3af',
                                        }}
                                        onClick={() => setLocalValue(time)}
                                      >
                                        <span className="font-mono select-none">{time}</span>
                                      </div>
                                    );
                                  })}
                                  <div style={{ height: itemHeight }}></div>
                                </div>
                              </div>
                              <div className="flex gap-2 w-full">
                                <button
                                  onClick={() => {
                                    onChange(localValue);
                                    setIsEditing(false);
                                  }}
                                  className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 active:scale-95 transition-all shadow-sm"
                                >
                                  אישור
                                </button>
                                <button
                                  onClick={() => setIsEditing(false)}
                                  className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-300 active:scale-95 transition-all"
                                >
                                  ביטול
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      };

                      const [editingStartTime, setEditingStartTime] = useState(false);
                      const [editingEndTime, setEditingEndTime] = useState(false);

                      const times: string[] = [];
                      if (businessHours && selectedDate) {
                        const [y, m, d] = selectedDate.split('-').map(Number);
                        const localDate = new Date(y, m - 1, d);
                        const dayOfWeek = localDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
                        const hours = businessHours?.[dayOfWeek];

                        if (hours && (hours.isActive || hours.is_active)) {
                          const startTime = hours.startTime || hours.start_time || '09:00';
                          const endTime = hours.endTime || hours.end_time || '17:00';
                          const startHour = Number(startTime.split(':')[0]);
                          const startMinute = Number(startTime.split(':')[1]);
                          const endHour = Number(endTime.split(':')[0]);
                          const endMinute = Number(endTime.split(':')[1]);

                          let h = startHour, m = startMinute;
                          while (h < endHour || (h === endHour && m < endMinute)) {
                            times.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
                            m += 5;
                            if (m >= 60) {
                              m = 0;
                              h += 1;
                            }
                          }
                        }
                      }

                      const startTimeValue = format(parseISO(currentAppointment.start_time), 'HH:mm', { locale: he });
                      const endTimeValue = format(parseISO(currentAppointment.end_time), 'HH:mm', { locale: he });

                      return (
                        <>
                          <InlineTimePicker
                            value={startTimeValue}
                            onChange={(newStart) => {
                              setSelectedTime(newStart);
                              setPendingChanges(prev => ({ ...prev, time: newStart }));
                              setShowEditConfirm(true);
                            }}
                            isEditing={editingStartTime}
                            setIsEditing={setEditingStartTime}
                            availableTimes={times.length > 0 ? times : [startTimeValue]}
                          />
                          <span className="text-gray-400">-</span>
                          <InlineTimePicker
                            value={endTimeValue}
                            onChange={(newEnd) => {
                              setSelectedEndTime(newEnd);
                              setPendingChanges(prev => ({ ...prev, end_time: newEnd }));
                              if (selectedTime && newEnd) {
                                const [startH, startM] = selectedTime.split(':').map(Number);
                                const [endH, endM] = newEnd.split(':').map(Number);
                                const duration = (endH * 60 + endM) - (startH * 60 + startM);
                                if (duration > 0) {
                                  setDurationInput(duration);
                                  setPendingChanges(prev => ({ ...prev, duration }));
                                }
                              }
                              setShowEditConfirm(true);
                            }}
                            isEditing={editingEndTime}
                            setIsEditing={setEditingEndTime}
                            availableTimes={times.length > 0 ? times : [endTimeValue]}
                          />
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

            {/* Service */}
            <div
              className={`flex items-center gap-2.5 p-3 bg-gray-50 rounded-lg transition-colors ${
                !canEdit ? 'opacity-60' : 'active:bg-gray-100 cursor-pointer'
              }`}
              onClick={() => !loading && canEdit && setEditingService(true)}
            >
              <Scissors className="h-4 w-4 text-gray-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 mb-0.5">שירות</p>
                <p className="text-sm font-medium text-gray-900 truncate">
                  {pendingChanges.service_name || currentAppointment.services?.name_he || currentAppointment.service_name}
                </p>
              </div>
            </div>

            {/* Staff */}
            <div
              className={`flex items-center gap-2.5 p-3 bg-gray-50 rounded-lg transition-colors ${
                !canEdit ? 'opacity-60' : 'active:bg-gray-100 cursor-pointer'
              }`}
              onClick={() => !loading && canEdit && setShowStaffSelector(true)}
            >
              <User className="h-4 w-4 text-gray-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 mb-0.5">איש צוות</p>
                <p className="text-sm font-medium text-gray-900 truncate">
                  {pendingChanges.staff_name || currentAppointment.users?.name || staffMembers.find(s => s.id === currentAppointment.staff_id)?.name || currentAppointment.staff_name || ''}
                </p>
              </div>
            </div>

            {/* Duration */}
            <div
              className={`flex items-center gap-2.5 p-3 bg-gray-50 rounded-lg transition-colors ${
                !canEdit ? 'opacity-60' : 'active:bg-gray-100 cursor-pointer'
              }`}
              onClick={() => !loading && canEdit && setEditingDuration(true)}
            >
              <Timer className="h-4 w-4 text-gray-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 mb-0.5">משך</p>
                {editingDuration && canEdit ? (
                  <input
                    type="number"
                    min={1}
                    value={durationInput === 0 ? '' : durationInput}
                    onChange={(e) => {
                      const val = e.target.value === '' ? '' : Number(e.target.value);
                      setDurationInput(val as any);
                      if (selectedTime && val && val > 0) {
                        const [h, m] = selectedTime.split(':').map(Number);
                        const start = new Date(selectedDate + `T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`);
                        const end = new Date(start.getTime() + val * 60000);
                        setSelectedEndTime(`${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`);
                      }
                      setPendingChanges(prev => ({ ...prev, duration: val }));
                    }}
                    onBlur={(e) => {
                      setEditingDuration(false);
                      const val = e.target.value === '' ? '' : Number(e.target.value);
                      if (val && val > 0 && val !== currentAppointment.duration) {
                        setPendingChanges(prev => ({ ...prev, duration: val }));
                        setShowEditConfirm(true);
                      }
                    }}
                    className="w-16 p-1 text-sm border border-gray-300 rounded"
                    autoFocus
                  />
                ) : (
                  <p className="text-sm font-medium text-gray-900">
                    {currentAppointment.duration > 0 ? currentAppointment.duration + ' דקות' : ''}
                  </p>
                )}
              </div>
            </div>

            {/* Price */}
            <div className="flex items-center gap-2.5 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-center w-4 h-4 flex-shrink-0">
                <span className="text-gray-500 text-sm">₪</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 mb-0.5">מחיר</p>
                <p className="text-sm font-semibold text-gray-900">
                  {benefits && loyaltyEnabled ? benefits.finalPrice : (
                    typeof currentAppointment.metadata?.price === 'number'
                      ? currentAppointment.metadata.price
                      : typeof currentAppointment.services?.price === 'number'
                      ? currentAppointment.services.price
                      : ''
                  )} ₪
                </p>
              </div>
            </div>

            {/* Phone */}
            <div className="flex items-center gap-2.5 p-3 bg-gray-50 rounded-lg col-span-2">
              <Phone className="h-4 w-4 text-gray-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 mb-0.5">טלפון</p>
                <a
                  href={`tel:${currentAppointment.customers?.phone || currentAppointment.customer_phone}`}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors direction-ltr text-right block"
                >
                  {currentAppointment.customers?.phone || currentAppointment.customer_phone}
                </a>
              </div>
            </div>

            {/* Loyalty Discount - if applicable */}
            {benefits && benefits.loyaltyDiscount > 0 && loyaltyEnabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowLoyaltyConfirm(true);
                }}
                className={`col-span-2 flex items-center justify-between p-3 rounded-lg border transition-colors ${
                  benefits.selectedBenefits.loyaltyDiscount
                    ? 'bg-amber-50 border-amber-200 text-amber-800'
                    : 'bg-gray-50 border-gray-200 text-gray-600 active:bg-gray-100'
                }`}
              >
                <span className="text-xs font-medium">
                  {benefits.selectedBenefits.loyaltyDiscount
                    ? `הנחת ${benefits.loyaltyLevel} פעילה`
                    : `הנחת ${benefits.loyaltyLevel} זמינה`}
                </span>
                <span className="text-sm font-bold">
                  {benefits.selectedBenefits.loyaltyDiscount ? '✓' : ''} -{benefits.loyaltyDiscount} ₪
                </span>
              </button>
            )}
          </div>
        </div>
        {/* History Section */}
        {logs && logs.length > 0 && (
          <div className="px-5 pb-5">
            <details className="group">
              <summary className="flex items-center justify-between cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-2">
                  <History className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">היסטוריה</span>
                </div>
                <span className="text-xs text-gray-500">({logs.length})</span>
              </summary>
              <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                {logs
                  // סינון כפילויות לפי action וערך חדש (new value)
              .filter((log, idx, arr) => {
                const key =
                  log.action +
                  '-' +
                  (log.details?.new_status ??
                    log.details?.new_time ??
                    log.details?.new_staff ??
                    log.details?.new_service ??
                    log.details?.new_duration ??
                    log.details?.new_phone ??
                    log.details?.points_added ??
                    log.details?.diamonds_added ??
                    '');
                return (
                  arr.findIndex(l =>
                    (l.action +
                      '-' +
                      (l.details?.new_status ??
                        l.details?.new_time ??
                        l.details?.new_staff ??
                        l.details?.new_service ??
                        l.details?.new_duration ??
                        l.details?.new_phone ??
                        l.details?.points_added ??
                        l.details?.diamonds_added ??
                        '')
                    ) === key
                  ) === idx
                );
              })
              .map((log) => (
                <div
                  key={log.id}
                  className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-200"
                >
                  <span className="text-xs text-gray-500 font-medium">
                    {format(parseISO(log.created_at), 'HH:mm', { locale: he })}
                  </span>
                  <div className="flex-1 text-xs text-gray-700">
                    {/* הצג את הלוגים בעברית בצורה ידידותית - רק הערך החדש */}
                    {(() => {
                      if (log.action === 'status_change') {
                        return (
                          <>
                            <span className="font-semibold">סטטוס:</span>
                            <span className="font-bold mx-1">{getStatusText(log.new_status)}</span>
                            {log.details.user_name && (
                              <span className="text-gray-500 mx-1">ע"י {log.details.user_name}</span>
                            )}
                            {log.details.reason && (
                              <span className="text-gray-400 mx-1">({log.details.reason})</span>
                            )}
                          </>
                        );
                      }
                      if (log.action === 'time_change') {
                        return (
                          <>
                            <span className="font-semibold">שעה:</span>
                            <span className="font-bold mx-1">{log.details.new_time}</span>
                            {log.details.user_name && (
                              <span className="text-gray-500 mx-1">ע"י {log.details.user_name}</span>
                            )}
                          </>
                        );
                      }
                      if (log.action === 'staff_change') {
                        return (
                          <>
                            <span className="font-semibold">איש צוות:</span>
                            <span className="font-bold mx-1">{log.details.new_staff}</span>
                            {log.details.user_name && (
                              <span className="text-gray-500 mx-1">ע"י {log.details.user_name}</span>
                            )}
                          </>
                        );
                      }
                      if (log.action === 'service_change') {
                        return (
                          <>
                            <span className="font-semibold">שירות:</span>
                            <span className="font-bold mx-1">{log.details.new_service}</span>
                            {log.details.user_name && (
                              <span className="text-gray-500 mx-1">ע"י {log.details.user_name}</span>
                            )}
                          </>
                        );
                      }
                      if (log.action === 'duration_change') {
                        return (
                          <>
                            <span className="font-semibold">משך:</span>
                            <span className="font-bold mx-1">{log.details.new_duration} דקות</span>
                            {log.details.user_name && (
                              <span className="text-gray-500 mx-1">ע"י {log.details.user_name}</span>
                            )}
                          </>
                        );
                      }
                      if (log.action === 'phone_change') {
                        return (
                          <>
                            <span className="font-semibold">טלפון:</span>
                            <span className="font-bold mx-1">{log.details.new_phone}</span>
                            {log.details.user_name && (
                              <span className="text-gray-500 mx-1">ע"י {log.details.user_name}</span>
                            )}
                          </>
                        );
                      }
                      if (log.action === 'loyalty_update') {
                        return (
                          <>
                            <span className="font-semibold">נאמנות:</span>
                            <span className="font-bold mx-1">{log.details.points_added} נק'</span>
                            {log.details.diamonds_added > 0 && (
                              <span className="font-bold mx-1">{log.details.diamonds_added} יהלומים</span>
                            )}
                            <span className="text-gray-400 mx-1">
                              (סה"כ: {log.details.total_points} נק', {log.details.total_diamonds} יהלומים)
                            </span>
                          </>
                        );
                      }
                      if (log.action === 'loyalty_discount_change') {
                        return (
                          <>
                            <span className="font-semibold">הנחת נאמנות:</span>
                            <span className="font-bold mx-1">
                              {log.details.discount_applied ? 'הופעלה' : 'בוטלה'}
                            </span>
                            {log.details.discount_applied && (
                              <span className="text-gray-400 mx-1">
                                (הנחה: {log.details.discount_amount}₪ | מחיר סופי: {log.details.final_price}₪)
                              </span>
                            )}
                            {log.details.user_name && (
                              <span className="text-gray-500 mx-1">ע"י {log.details.user_name}</span>
                            )}
                          </>
                        );
                      }
                      return (
                        <span>
                          {log.action}
                          {log.details?.user_name && (
                            <span className="text-gray-500 mx-1">ע"י {log.details.user_name}</span>
                          )}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              ))}
              </div>
            </details>
          </div>
        )}
        </div>

        {/* Action Buttons - Fixed at bottom */}
        <div className="border-t border-gray-100 px-5 py-3 bg-white rounded-b-xl">
          <div className="flex items-center gap-2 overflow-x-auto">
            {/* הצגת כפתור חשבונית בלבד כאשר יש invoiceUrl */}
            {invoiceUrl && (
              <a
                href={invoiceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                <span>חשבונית</span>
              </a>
            )}
            {currentAppointment.status === 'booked' && (
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleConfirm}
                className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors whitespace-nowrap"
              >
                <Check className="h-4 w-4" />
                <span>אשר</span>
              </motion.button>
            )}

            {currentAppointment.status === 'confirmed' && (
              <>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={handleMarkNoShow}
                  className="flex items-center gap-1.5 px-3 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 transition-colors whitespace-nowrap"
                >
                  <UserX className="h-4 w-4" />
                  <span>לא הגיע</span>
                </motion.button>

                {hasPaymentTerminal ? (
                  <>
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowChargeModal(true)}
                      disabled={localStorage.getItem('selectedPlan') === 'basic'}
                      className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                        localStorage.getItem('selectedPlan') === 'basic'
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      <span>חייב</span>
                    </motion.button>
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={handleMarkCompleted}
                      className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors whitespace-nowrap"
                    >
                      <Check className="h-4 w-4" />
                      <span>הושלם</span>
                    </motion.button>
                  </>
                ) : (
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={handleMarkCompleted}
                    className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors whitespace-nowrap"
                  >
                    <Check className="h-4 w-4" />
                    <span>הושלם</span>
                  </motion.button>
                )}
              </>
            )}

            {currentAppointment.status === 'completed' && currentAppointment.metadata?.invoice_url && (
              <a
                href={currentAppointment.metadata.invoice_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                <span>חשבונית</span>
              </a>
            )}

            {currentAppointment.status === 'canceled' && (
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleMarkBooked}
                className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                <Calendar className="h-4 w-4" />
                <span>החזר</span>
              </motion.button>
            )}

            {!['canceled', 'no_show', 'completed'].includes(currentAppointment.status) && (
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowCancelConfirm(true)}
                className="flex items-center gap-1.5 px-3 py-2 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600 transition-colors whitespace-nowrap"
              >
                <Ban className="h-4 w-4" />
                <span>ביטול</span>
              </motion.button>
            )}
          </div>
        </div>

        {/* Staff Selector Modal */}
        <AnimatePresence>
          {showStaffSelector && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]"
              onClick={() => setShowStaffSelector(false)}
            >
              <motion.div
                initial={{ scale: 0.95, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 20 }}
                className="bg-white rounded-lg p-4 w-full max-w-xs"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="space-y-2">
                  {staffMembers.map((staff) => (
                    <motion.button
                      key={staff.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleStaffSelect(staff.id, staff.name)}
                      disabled={loading}
                      className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-medium">{staff.name}</span>
                      {staff.price && (
                        <div className="flex items-center gap-1 text-gray-500">
                          <span>₪{staff.price}</span>
                        </div>
                      )}
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Service Selector Modal */}
        <AnimatePresence>
          {editingService && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]"
              onClick={() => setEditingService(false)}
            >
              <motion.div
                initial={{ scale: 0.95, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 20 }}
                className="bg-white rounded-lg p-4 w-full max-w-xs"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="space-y-2">
                  {services.map((service) => (
                    <motion.button
                      key={service.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleServiceSelect(service)}
                      disabled={loading}
                      className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-medium">{service.name_he}</span>
                      <span className="text-gray-500">{service.duration} דק'</span>
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Edit Confirmation Modal */}
        <AnimatePresence>
          {showEditConfirm && Object.keys(pendingChanges).length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]"
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setShowEditConfirm(false);
                  setPendingChanges({});
                }
              }}
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="bg-white rounded-xl p-6 max-w-md w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-lg font-semibold mb-4">אישור שינויים</h3>
                
                <div className="space-y-4 mb-6">
                  {pendingChanges.date && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-gray-400" />
                      <div>
                        <div className="text-sm text-gray-500">תאריך חדש:</div>
                        <div className="font-medium">{format(parseISO(pendingChanges.date), 'EEEE, d בMMMM yyyy', { locale: he })}</div>
                      </div>
                    </div>
                  )}
                  
                  {pendingChanges.time && (
                    <div className="flex itemscenter gap-2">
                      <Clock className="h-5 w-5 text-gray-400" />
                      <div>
                        <div className="text-sm text-gray-500">שעה חדשה:</div>
                        <div className="font-medium">{pendingChanges.time}</div>
                      </div>
                    </div>
                  )}

                  {pendingChanges.staff_name && (
                    <div className="flex items-center gap-2">
                      <User className="h-5 w-5 text-gray-400" />
                      <div>
                        <div className="text-sm text-gray-500">איש צוות חדש:</div>
                        <div className="font-medium">{pendingChanges.staff_name}</div>
                      </div>
                    </div>
                  )}

                  {pendingChanges.service_name && (
                    <div className="flex items-center gap-2">
                      <Scissors className="h-5 w-5 text-gray-400" />
                      <div>
                        <div className="text-sm text-gray-500">שירות חדש:</div>
                        <div className="font-medium">{pendingChanges.service_name}</div>
                      </div>
                    </div>
                  )}

                  {pendingChanges.customer_phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-5 w-5 text-gray-400" />
                      <div>
                        <div className="text-sm text-gray-500">טלפון חדש:</div>
                        <div className="font-medium">{pendingChanges.customer_phone}</div>
                      </div>
                    </div>
                  )}

                  {pendingChanges.duration !== undefined && (
                    <div className="flex items-center gap-2">
                      <Timer className="h-5 w-5 text-gray-400" />
                      <div>
                        <div className="text-sm text-gray-500">משך שירות חדש:</div>
                        <div className="font-medium">{pendingChanges.duration} דקות</div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-4">
                  <button
                    onClick={() => {
                      setShowEditConfirm(false);
                      setPendingChanges({});
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    ביטול
                  </button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      // אם אין שינוי אמיתי, סגור את המודל בלי לשמור
                      const hasRealChange = Object.entries(pendingChanges).some(
                        ([key, val]) => val !== undefined && val !== null && val !== ''
                      );
                      if (!hasRealChange) {
                        setShowEditConfirm(false);
                        setPendingChanges({});
                        return;
                      }
                      handleSaveChanges();
                    }}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>שומר...</span>
                      </>
                    ) : (
                      <>
                        <Check className="h-5 w-5" />
                        <span>שמור שינויים</span>
                      </>
                    )}
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Cancel Confirmation Modal */}
        <AnimatePresence>
          {showCancelConfirm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]"
              onClick={(e) => {
                if (e.target === e.currentTarget) setShowCancelConfirm(false);
              }}
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="bg-white rounded-xl p-6 max-w-md w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-lg font-semibold mb-4">אישור ביטול תור</h3>
                <p className="text-gray-500 text-sm mb-4">
                  האם אתה בטוח שברצונך לבטל את התור הזה? פעולה זו לא ניתן לשנות.
                </p>
                <div className="flex justify-end gap-4">
                  <button
                    onClick={() => setShowCancelConfirm(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    ביטול
                  </button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleCancel}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>מבצע ביטול...</span>
                      </>
                    ) : (
                      <>
                        <Ban className= "h-5 w-5" />
                        <span>בטל תור</span>
                      </>
                    )}
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Date Selector Modal - תיקון: סוגריים חסרים, className, וודא שהמודאל מחוץ ל-header */}
        <AnimatePresence>
          {showDateSelector && !isCompleted && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[70] flex items-center justify-center bg-black/30"
              onClick={e => {
                if (e.target === e.currentTarget) setShowDateSelector(false);
              }}
            >
              <motion.div
                initial={{ scale: 0.95, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 20 }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-sm flex flex-col"
                style={{ height: '70vh', maxHeight: '70vh' }}
                onClick={e => e.stopPropagation()}
              >
                {
                  // הדפס לקונסול את שעות הפעילות של כל יום ברשימה
                  (() => {
                    Array.from({ length: 14 }).forEach((_, i) => {
                      const dateObj = new Date();
                      dateObj.setHours(12, 0, 0, 0); // עוגן באמצע היום
                      dateObj.setDate(dateObj.getDate() + i);
                      const dateStr = format(dateObj, 'yyyy-MM-dd');
                      const [y, m, d] = dateStr.split('-').map(Number);
                      const localDate = new Date(y, m - 1, d);
                      const dayOfWeek = localDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
                      const hours = businessHours?.[dayOfWeek];

                    });
                    return null;
                  })()
                }
                <div className="px-5 py-4 border-b border-gray-100 text-center">
                  <h3 className="text-lg font-semibold text-gray-900">בחר תאריך</h3>
                  <button
                    onClick={() => setShowDateSelector(false)}
                    className="absolute top-4 left-4 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
                  >
                    <X className="h-5 w-5 text-gray-400" />
                  </button>
                </div>
                {/* גלילה אנכית מודרנית של ימים (כפתור אחד בכל שורה, לא grid) */}
                <div className="flex-1 overflow-y-auto px-5 py-3">
                <div className="flex flex-col gap-2">
                  {Array.from({ length: 14 }).map((_, i) => {
                    const dateObj = new Date();
                    dateObj.setHours(12, 0, 0, 0);
                    dateObj.setDate(dateObj.getDate() + i);
                    const dateStr = format(dateObj, 'yyyy-MM-dd');
                    const isOpen = isBusinessDayOpen(dateStr);
                    const isSelected = selectedDate === dateStr;
                    return (
                      <button
                        key={dateStr}
                        type="button"
                        disabled={!isOpen}
                        onClick={() => {
                          if (isOpen) {
                            setSelectedDate(dateStr);
                            setPendingChanges(prev => ({ ...prev, date: dateStr }));
                            setShowDateSelector(false);
                            setShowEditConfirm(true);
                          }
                        }}
                        className={`
                          flex items-center justify-between px-4 py-3 rounded-lg transition-all
                          text-sm font-medium
                          ${isOpen
                            ? (isSelected
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-50 text-gray-900 hover:bg-gray-100 active:bg-gray-200')
                            : 'bg-gray-50 text-gray-400 opacity-50 cursor-not-allowed'}
                `}
                        style={{
                          margin: 0,
                          minHeight: 48,
                          boxSizing: 'border-box'
                        }}
                      >
                        <span className="truncate text-right" style={{ flex: 1 }}>
                          {format(dateObj, 'EEEE, d בMMMM', { locale: he })}
                        </span>
                      </button>
                    );
                  })}
                </div>
                </div>
                <div className="border-t border-gray-100 px-5 py-3">
                  <button
                    type="button"
                    className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 active:bg-gray-300 text-sm font-medium transition-colors"
                    onClick={() => setShowDateSelector(false)}
                  >
                    ביטול
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Loyalty Discount Confirmation Dialog */}
        <AnimatePresence>
          {showLoyaltyConfirm && benefits && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[70] flex items-center justify-center bg-black/30"
              onClick={() => setShowLoyaltyConfirm(false)}
            >
              <motion.div
                initial={{ scale: 0.95, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 20 }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-5 py-4 border-b border-gray-100 text-center">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {benefits.selectedBenefits.loyaltyDiscount ? 'ביטול הנחת נאמנות' : 'הפעלת הנחת נאמנות'}
                  </h3>
                </div>

                <div className="px-5 py-4">
                  <div className="bg-amber-50 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">דרגת נאמנות:</span>
                      <span className="text-sm font-semibold text-amber-700">{benefits.loyaltyLevel}</span>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">סכום הנחה:</span>
                      <span className="text-sm font-semibold text-amber-700">{benefits.loyaltyDiscount} ₪</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">מחיר לאחר הנחה:</span>
                      <span className="text-sm font-bold text-gray-900">
                        {benefits.selectedBenefits.loyaltyDiscount
                          ? benefits.basePrice + ' ₪'
                          : (benefits.basePrice - benefits.loyaltyDiscount) + ' ₪'
                        }
                      </span>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 text-center">
                    {benefits.selectedBenefits.loyaltyDiscount
                      ? 'האם לבטל את הנחת הנאמנות?'
                      : 'האם להפעיל את הנחת הנאמנות?'
                    }
                  </p>
                </div>

                <div className="border-t border-gray-100 px-5 py-3 flex gap-2">
                  <button
                    type="button"
                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 active:bg-gray-300 text-sm font-medium transition-colors"
                    onClick={() => setShowLoyaltyConfirm(false)}
                  >
                    ביטול
                  </button>
                  <button
                    type="button"
                    className="flex-1 px-4 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 active:bg-amber-800 text-sm font-medium transition-colors"
                    onClick={async () => {
                      setShowLoyaltyConfirm(false);
                      await toggleLoyaltyDiscount();
                    }}
                  >
                    {benefits.selectedBenefits.loyaltyDiscount ? 'בטל הנחה' : 'אשר הנחה'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Charge Customer Flow Modal - ודא שהקומפוננטה ChargeCustomerFlow מוצגת מחוץ ל־motion.div הראשי, ולא בתוך ה־modal של התור. */}
        {showChargeModal && (
          <ChargeCustomerFlow
            onClose={() => setShowChargeModal(false)}
            onSuccess={handleChargeSuccess}
            appointmentId={currentAppointment.id}
            defaultAppointment={{
              ...currentAppointment,
              metadata: {
                ...currentAppointment.metadata,
                // ודא שמחיר נשלח נכון: סדר עדיפויות metadata.price > staff_services.price > services.price > appointment.price > 0
                price:
                  typeof currentAppointment.metadata?.price === 'number'
                    ? currentAppointment.metadata.price
                    : typeof benefits?.basePrice === 'number' && benefits?.basePrice > 0
                    ? benefits.basePrice
                    : typeof currentAppointment.services?.price === 'number'
                    ? currentAppointment.services.price
                    : typeof currentAppointment.price === 'number'
                    ? currentAppointment.price
                    : 0
              }
            }}
            defaultChargeType="appointment"
            skipCustomerStep={true}
            skipChargeTypeStep={true}
            defaultCustomer={{
              customerId: currentAppointment.customers?.id || currentAppointment.customer_id || '',
              customerName: currentAppointment.customers?.name || currentAppointment.customer_name || '',
              customerPhone: currentAppointment.customers?.phone || currentAppointment.customer_phone || '',
              customerEmail: currentAppointment.customers?.email || '',
            }}
            businessId={currentAppointment.business_id}
            disableBackOnStep={2}
          />
        )}
      </motion.div>
    </motion.div>
  );
}